import { FC, useEffect, useRef } from 'react';

import styled from '@emotion/styled';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import type {
  AnimationConfigWithData,
  AnimationItem,
  RendererType,
} from 'lottie-web';

const Container = styled.div`
  > svg {
    &:not(:last-of-type) {
      display: none;
    }
  }
`;

interface IProps<T extends RendererType> {
  fromURL: string;
  animationParams: Omit<
    AnimationConfigWithData<T>,
    'container' | 'animationData'
  >;
  StaticSVG: FC<Omit<SvgIconProps, 'color'>>;
  className?: string;
  onAnimationReady?: (animationItem: AnimationItem) => void;
}

export const Lottie = <T extends RendererType>({
  fromURL,
  animationParams,
  StaticSVG,
  className,
  onAnimationReady,
}: IProps<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let animation: AnimationItem | undefined = undefined;
    let destroyed = false;

    // Async import of all lottie components:
    // lottie-web library
    // dotlottie library
    // dotlottie.fromURL to load and decode .lottie file
    const lottieWebPromise = import('lottie-web');
    import('@dotlottie/dotlottie-js')
      .then(async ({ DotLottie }) => {
        const [lottieWeb, dotLottie] = await Promise.all([
          lottieWebPromise,
          new DotLottie().fromURL(fromURL).catch(() => null),
        ]);

        if (!dotLottie || destroyed) {
          return;
        }

        animation = lottieWeb.default.loadAnimation({
          container,
          animationData: dotLottie.animations[0]?.data,
          ...animationParams,
        });
        onAnimationReady?.(animation);
      })
      .catch((e) => {
        if (e.name !== 'ChunkLoadError') {
          throw e;
        }
      });

    return () => {
      animation?.destroy();
      destroyed = true;
    };
  }, []);

  return (
    <Container ref={containerRef} className={className}>
      <StaticSVG style={{ width: '100%', height: '100%' }} />
    </Container>
  );
};
